/**
 * The frame of a consent ask that takes the whole page: first run's AI step, the gate that stops an existing user,
 * and the analytics step (#709). One component, so the two steps of first run cannot differ in what surrounds them.
 *
 * **The panel is the artboard's** (`rgba(28,24,22,0.82)`, 24 px radius, 48 px padding, 640 px) from `md` up, not first
 * run's lighter wash (`firstrun/panel.ts`): measured in a browser, the failure line (`--warn-terracotta-text`) reads
 * 3.3:1 on the wash and 5.7:1 on the artboard's fill. Below `md` it is full-bleed, as every first-run screen is on a
 * phone, with the buttons on the floor, **on a dark veil of its own** (the page gradient bare behind it put the text at
 * 3.2 to 4.5:1 at 390 px wide); from `md` the card centres, because pinning the buttons to the floor of a tablet put
 * them 600 px from the question.
 *
 * Below `md` there is no card, so the screen is its own ground: the dark recess (`RECESS`) over the page gradient. Bare,
 * the text crossed the gradient's tan peak and read 3.2 to 4.5:1 at 390 wide (measured in a browser); on the recess the
 * smallest line measures 6.1. It is a custom property, not an inline background, so that `md:bg-transparent` can still
 * hand the screen to the card.
 *
 * The entrance is a 12 px rise with the fade, 400 ms (AA-D8); under Reduce Motion the fade alone.
 */

import { motion, useReducedMotion } from 'motion/react'
import type { CSSProperties, ReactNode } from 'react'

import { RECESS } from './grounds'

const EASE = [0.16, 1, 0.3, 1] as const

interface Props {
  children: ReactNode
  /** Rendered in the page's `<main>` beside the panel, for what is portalled out of it (the full text). */
  aside?: ReactNode
}

export function AskStepFrame({ children, aside }: Props) {
  const reduce = !!useReducedMotion()
  return (
    <div
      className="min-h-[100dvh] bg-(--consent-veil) md:bg-transparent"
      style={{ '--consent-veil': RECESS } as CSSProperties}
    >
      <main className="mx-auto flex min-h-[100dvh] w-full max-w-[520px] flex-col px-6 pt-6 md:max-w-[640px] md:py-10">
        <motion.div
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduce ? 0.12 : 0.4, ease: EASE }}
          className="flex flex-1 flex-col md:my-auto md:flex-none md:rounded-3xl md:border md:border-(--text-cream-border) md:bg-[rgba(28,24,22,0.82)] md:px-12 md:py-12 md:shadow-[0_24px_48px_-20px_rgba(20,14,12,0.55)]"
        >
          {children}
        </motion.div>
        {aside}
      </main>
    </div>
  )
}
